import { Folder, Layer } from '../../store/cop/models';

export class FolderUtils {
  static findFolder(items: Folder[], predicate: (value: Folder, parent: Folder) => boolean): Folder {
    if (!items || items.length < 1) {
      return null;
    }
    const queue = [] as Folder[][];
    items.forEach(item => queue.push([null, item]));
    while (queue.length > 0) {
      const poppedItem = queue.shift();
      if (predicate(poppedItem[1], poppedItem[0])) {
        return poppedItem[1];
      }
      const folder = poppedItem[1];
      if (folder.folders && folder.folders.length) {
        folder.folders.forEach(item => queue.push([folder, item]));
      }
    }
    return null;
  }

  static findFolders(items: Folder[], predicate: (value: Folder, parent: Folder) => boolean): Folder[] {
    if (!items || items.length < 1) {
      return null;
    }
    const foundFolders: Folder[] = [];
    const queue = [] as Folder[][];
    items.forEach(item => queue.push([null, item]));
    while (queue.length > 0) {
      const poppedItem = queue.shift();
      if (predicate(poppedItem[1], poppedItem[0])) {
        foundFolders.push(poppedItem[1]);
      }
      const folder = poppedItem[1];
      if (folder.folders && folder.folders.length) {
        folder.folders.forEach(item => queue.push([folder, item]));
      }
    }
    return foundFolders.length === 0 ? null : foundFolders;
  }

  static findFolderWithLayer(items: Folder[], predicate: (value: Layer, parent: Folder) => boolean) {
    if (!items || items.length < 1) {
      return null;
    }
    const queue = [] as (Layer | Folder)[][];
    items.forEach(item => queue.push([null, item]));
    while (queue.length > 0) {
      const poppedItem = queue.shift();
      const itemAsAny = poppedItem[1] as any;
      // Detect layers and call predicate when we think it is a Layer
      if (itemAsAny.folders === undefined && itemAsAny.layers === undefined) {
        if (predicate(poppedItem[1] as Layer, poppedItem[0] as Folder)) {
          return poppedItem[0] as Folder;
        }
        continue;
      }
      const folder = poppedItem[1] as Folder;
      if (folder.folders && folder.folders.length) {
        folder.folders.forEach(item => queue.push([folder, item]));
      }
      if (folder.layers && folder.layers.length) {
        folder.layers.forEach(item => queue.push([folder, item]));
      }
    }
    return null;
  }

  static getAllLayersInFolder = (folder: Folder, layersFound?: Layer[]): Layer[] => {
    const layers = layersFound || ([] as Layer[]);

    for (const childLayer of folder.layers) {
      layers.push(childLayer);
    }

    for (const childFolder of folder.folders) {
      this.getAllLayersInFolder(childFolder, layers);
    }

    return layers;
  };

  static findLayer(items: Folder[], predicate: (value: Layer, parent: Folder) => boolean) {
    if (!items || items.length < 1) {
      return null;
    }
    const queue = [] as Folder[][];
    items.forEach(item => queue.push([null, item]));
    while (queue.length > 0) {
      const poppedItem = queue.shift();
      const folder = poppedItem[1] as Folder;
      if (folder.layers && folder.layers.length) {
        const foundLayer = folder.layers.find(item => predicate(item, folder));
        if (foundLayer) {
          return foundLayer;
        }
      }
      if (folder.folders && folder.folders.length) {
        folder.folders.forEach(item => queue.push([folder, item]));
      }
    }
    return null;
  }

  static findLayers(items: Folder[], predicate: (value: Layer, parent: Folder) => boolean): Layer[] {
    if (!items || items.length < 1) {
      return [];
    }
    const foundLayers = [] as Layer[];
    const queue = [] as Folder[][];
    items.forEach(item => queue.push([null, item]));
    while (queue.length > 0) {
      const poppedItem = queue.shift();
      const folder = poppedItem[1] as Folder;
      if (folder.layers && folder.layers.length) {
        const foundLayerAr = folder.layers.filter(item => predicate(item, folder));
        if (foundLayerAr) {
          foundLayerAr.forEach(f => foundLayers.push(f));
        }
      }
      if (folder.folders && folder.folders.length) {
        folder.folders.forEach(item => queue.push([folder, item]));
      }
    }
    return foundLayers;
  }

  static mapFolders(
    items: Folder[],
    folderDelegate?: (value: Folder, parent: Folder) => Folder,
    layerDelegate?: (value: Layer, parent: Folder) => Layer
  ) {
    if (!items || items.length < 1) {
      return null;
    }
    const resultFolders = [] as Folder[];
    const parentsState = [] as Folder[];
    const queue = [] as Folder[][];
    items.slice().forEach(item => queue.push([null, item]));

    while (queue.length > 0) {
      const poppedItem = queue.shift();

      let parent = null as Folder;
      while (parent == null && parentsState.length > 0) {
        const poppedParent = parentsState.shift();
        if (poppedItem[0] && poppedItem[0].uId === poppedParent.uId) {
          parent = poppedParent;
          parentsState.unshift(poppedParent);
        }
      }

      let newFolder = null;
      try {
        newFolder = folderDelegate ? folderDelegate({ ...poppedItem[1] }, parent) : { ...poppedItem[1] };
      } catch (err) {
        const newError = new Error('Folder predicate threw an exception in mapFolders: ' + err.message);
        (newError as any).innerException = err;
        throw newError;
      }
      if (newFolder) {
        let newLayers = null;
        if (newFolder.layers && newFolder.layers.length) {
          newLayers = [];
          const sourceLayers = newFolder.layers.slice();
          while (sourceLayers.length > 0) {
            const shiftedLayer = sourceLayers.shift();
            const newLayer = layerDelegate ? layerDelegate({ ...shiftedLayer }, newFolder) : shiftedLayer;
            if (newLayer) {
              newLayers.push(newLayer);
            }
          }
          newFolder.layers = newLayers.length > 0 ? newLayers : null;
        }

        if (!parent) {
          resultFolders.push(newFolder);
        } else {
          if (!parent.folders) {
            parent.folders = [];
          }
          parent.folders.push(newFolder);
        }

        // const folder = poppedItem[1] as Folder;
        if (newFolder.folders && newFolder.folders.length) {
          parentsState.push(newFolder);
          newFolder.folders.slice().forEach(item => queue.push([newFolder, item]));
          newFolder.folders = [];
        }
      }
    }
    return resultFolders;
  }

  static reduceMapFolderTree<T>(
    items: Folder[],
    folderHandler: (value: Folder, parent: Folder | null, state: T) => void,
    layerHandler: (value: Layer, parent: Folder | null, state: T) => void,
    state: T
  ): T {
    if (!items || items.length < 1) {
      return null;
    }
    const parentsState = [] as Folder[];
    const queue = [] as Folder[][];
    items.forEach(item => queue.push([null, item]));

    while (queue.length > 0) {
      const poppedItem = queue.shift();

      let parent = null as Folder;
      while (parent == null && parentsState.length > 0) {
        const poppedParent = parentsState.pop();
        if (poppedItem[0] && poppedItem[0].uId === poppedParent.uId) {
          parent = poppedParent;
          parentsState.push(poppedParent);
        }
      }

      let folder = poppedItem[1];
      try {
        folderHandler(poppedItem[1], parent, state);
      } catch (err) {
        const newError = new Error('Folder handler threw an exception in mapFolders: ' + err.message);
        (newError as any).innerException = err;
        throw newError;
      }
      if (folder) {
        if (folder.layers && folder.layers.length) {
          const layers = folder.layers.slice();
          while (layers.length > 0) {
            const shiftedLayer = layers.shift();
            layerHandler(shiftedLayer, folder, state);
          }
        }

        // const folder = poppedItem[1] as Folder;
        if (folder.folders && folder.folders.length) {
          parentsState.push(folder);
          folder.folders
            .slice()
            .reverse()
            .forEach(item => queue.push([folder, item]));
        }
      }
    }
    return state;
  }

  static getLayerSection(layer: Layer, foldersSet: { [folderUId: string]: Folder }): Folder {
    if (!!!foldersSet || !!!layer) {
      return null;
    }
    let folder = foldersSet[layer.folderUId];

    while (!!folder) {
      if (folder.sectionType > 0) {
        return folder;
      }
      folder = !!folder.parentUId ? foldersSet[folder.parentUId] : null;
    }

    return null;
  }
}
